import Vue from 'vue';
import toast from '@/services/toasts/index.js';
import { success, fail } from '@helpers/result-helper.js'
import { VuexBuilder } from '@helpers/vuexBuilder';

const state = {
	assets: [],
	assets_loading: false,
	assets_loaded: false,
	assets_isSaving: false,
	assets_isDeleting: false,
	assets_isAssigning: false,
	operations: {
		get: false,
		list: false,
		saving: false,
		deleting: false,
		assigning: false,
		uploading: false,
		downloading: false,
		deletingFile: false,
	},

};

const getters = {
	assets: state => state.assets,
	hasLoaded: state => state.assets_loaded,
	isGetting: state => state.operations.get,
	isUploading: state => state.operations.uploading,
	isDownloading: state => state.operations.downloading,
	isDeletingFile: state => state.operations.deletingFile,
	isSaving: state => state.operations.saving,
	isDeleting: state => state.operations.deleting,
	isAssigning: state => state.operations.assigning,
	getAssetsByLocation: state => locationId => state.assets.filter(x => x.currentLocationId === locationId),
	getAssetsByCategory: state => categoryId => state.assets.filter(x => x.categoryId === categoryId),
	getById: state => id => state.assets.find(x => x.id === id)
};

const actions = {
	async get({ commit, rootState }) {
		return await new VuexBuilder(commit)
			.operationKey("list")
			.request(() => Vue.axios.get(`/api/${rootState.tenantId}/assets`))
			.commitData('GET_SUCCESS')
			.execute()
	},
	async getDetailsById({commit, rootState}, assetId) {
		return await new VuexBuilder(commit)
			.operationKey("get")
			.request(() => Vue.axios.get(`/api/${rootState.tenantId}/assets/${assetId}`))
			.commitData('UPDATE_SUCCESS')
			.execute()
	},
	async create({ commit, rootState }, asset) {
		return await new VuexBuilder(commit)
			.operationKey("saving")
			.request(() => Vue.axios.post(`/api/${rootState.tenantId}/assets/`, asset))
			.commitData('CREATE_SUCCESS')
			.successToast('Asset created successfully.')
			.errorToast('Failed to create asset.')
			.execute()
	},
	async update({ commit, rootState }, asset) {
		return await new VuexBuilder(commit)
			.operationKey("saving")
			.request(() => Vue.axios.put(`/api/${rootState.tenantId}/assets/${asset.id}`, asset))
			.commitData('UPDATE_SUCCESS')
			.successToast('Asset updated successfully.')
			.errorToast((err) => err.response.status === 409
				? toast.error('Failed to update asset. Asset No is already in use')
				: toast.error('Failed to update asset.'))
			.execute()
	},
	async delete({ commit, rootState }, id) {
		return await new VuexBuilder(commit)
			.operationKey("deleting")
			.request(() =>  Vue.axios.delete(`/api/${rootState.tenantId}/assets/${id}`))
			.commitData('DELETE_SUCCESS', id)
			.successToast('Asset deleted successfully.')
			.errorToast('Failed to delete asset.')
			.execute()
	},
	async duplicate({commit, rootState}, id) {
		return await new VuexBuilder(commit)
			.operationKey("saving")
			.request(() =>  Vue.axios.post(`/api/${rootState.tenantId}/assets/${id}/duplicate`))
			.commitData('UPDATE_SUCCESS')
			.successToast('Asset duplicated successfully.')
			.errorToast('Failed to duplicate asset.')
			.execute()
	},
	async assign({ commit, rootState }, payload) {
		return await new VuexBuilder(commit)
			.operationKey("assigning")
			.request(() =>  Vue.axios.post(`/api/${rootState.tenantId}/assets/assign`, payload))
			.commitData('UPDATE_SUCCESS')
			.errorToast('Failed to assign asset.')
			.execute()
	},
	async unassign({ commit, rootState }, payload) {
		return await new VuexBuilder(commit)
			.operationKey("saving")
			.request(() =>  Vue.axios.post(`/api/${rootState.tenantId}/assets/unassign`, payload))
			.commitData('UPDATE_SUCCESS')
			.successToast('Asset unassigned successfully.')
			.errorToast('Failed to unassign asset.')
			.execute()
	},
	async retire({ commit, rootState }, payload) {
		return await new VuexBuilder(commit)
			.operationKey("saving")
			.request(() =>  Vue.axios.post(`/api/${rootState.tenantId}/assets/retire`, payload))
			.commitData('UPDATE_SUCCESS')
			.successToast('Asset retired.')
			.errorToast('Failed to retire asset.')
			.execute()
	},
	async activate({ commit, rootState }, payload) {
		return await new VuexBuilder(commit)
			.operationKey("saving")
			.request(() =>  Vue.axios.post(`/api/${rootState.tenantId}/assets/activate`, payload))
			.commitData('UPDATE_SUCCESS')
			.successToast('Asset activated.')
			.errorToast('Failed to activate asset.')
			.execute()
	},
	async service({ commit, rootState }, payload) {
		return await new VuexBuilder(commit)
			.operationKey("saving")
			.request(() =>  Vue.axios.post(`/api/${rootState.tenantId}/assets/service`, payload))
			.commitData('UPDATE_SUCCESS')
			.successToast('Asset serviced.')
			.errorToast('Failed to mark asset as serviced.')
			.execute()
	},
	async updateBulk({commit, rootState }, bulkUpdateDto) {
		return await new VuexBuilder(commit)
			.operationKey('saving')
			.request(() => Vue.axios.put(`/api/${rootState.tenantId}/assets/update-list`, bulkUpdateDto))
			.successToast('Assets updated successfully')
			.errorToast('Failed to update assets')
			.commitData('UPDATE_SUCCESS')
			.execute()
	},
	async uploadFiles({commit, rootState}, uploadDto) {
		uploadDto.document.append('type', 0); // general file

		return await new VuexBuilder(commit)
			.operationKey('uploading')
			.request(() => Vue.axios.post(`/api/${rootState.tenantId}/assets/${uploadDto.assetId}/files`, uploadDto.document, {
				headers: { 'Content-Type': 'multipart/form-data' }
			}))
			.successToast('Asset file(s) uploaded successfully')
			.errorToast('Failed to upload file(s)')
			.transformData(data => data.map(x => { return {...x, assetId: uploadDto.assetId } }))
			.commitData('UPLOAD_SUCCESS')
			.execute()
	},
	async downloadFile({commit, rootState}, downloadDto) {
		await new VuexBuilder(commit)
			.operationKey('downloading')
			.request(() => Vue.axios({
				url: `/api/${rootState.tenantId}/assets/${downloadDto.assetId}/Files/${downloadDto.fileId}`,
				method: 'GET',
				responseType: 'blob'
			}))
			.errorToast("Failed to download file(s)")
			.download(downloadDto.fileName)
	},
	async downloadCsvList({commit, rootState}) {
		await new VuexBuilder(commit)
			.operationKey('downloading')
			.request(() => Vue.axios({
				url: `/api/${rootState.tenantId}/assets/csv`,
				method: 'GET',
				responseType: 'blob'
			}))
			.errorToast("Failed to download csv list")
			.download("assets.csv")
	},
	async deleteFile({commit, rootState}, deleteDto) {
		return await new VuexBuilder(commit)
			.operationKey('deletingFile')
			.request(() => Vue.axios.delete(`/api/${rootState.tenantId}/assets/${deleteDto.assetId}/files/${deleteDto.fileId}`))
			.successToast('Asset file(s) deleted successfully')
			.errorToast('Failed to delete file(s)')
			.commitData('FILE_DELETE', deleteDto)
			.execute()
	}
};

const mutations = {
	START_OPERATION(state, operationKey) {
		state.operations[operationKey] = true
	},
	FINISH_OPERATION(state, operationKey) {
		state.operations[operationKey] = false
	},
	GET_PENDING(state) {
		state.assets_loading = true;
	},
	GET_SUCCESS(state, data) {
		state.assets_loading = false;
		state.assets_loaded = true;
		state.assets = data;
	},
	GET_FAILURE(state) {
		state.assets_loading = false;
	},
	CREATE_SUCCESS(state, newAsset) {
		state.assets.push(newAsset);
	},
	UPDATE_SUCCESS(state, updatedAsset) {
		const updates = Array.isArray(updatedAsset) ? updatedAsset : [updatedAsset];
		for (const update of updates) {
			const index = state.assets.findIndex(x => x.id === update.id);
			if (index > -1) {
				state.assets.splice(index, 1, update)
			}
			else {
				state.assets.push(update)
			}
		}
	},
	DELETE_SUCCESS(state, id) {
		const index = state.assets.findIndex(a => a.id === id);
		if (index > -1) {
			state.assets.splice(index, 1);
		}
		state.assets_isDeleting = false;
	},
	UPLOAD_SUCCESS(state, uploads) {
		for (const upload of uploads) {
			// need to then perform a add or replace on files
			const index = state.assets.findIndex(x => x.id === upload.assetId);
			if (index === -1) continue;
			state.assets[index].files.push(upload)
		}
	},
	FILE_DELETE(state, deleteDto) {
		const index = state.assets.findIndex(a => a.id === deleteDto.assetId);
		if (index === -1) return;

		const fileIndex = state.assets[index].files.findIndex(x => x.id === deleteDto.fileId)
		if (fileIndex === -1) return;

		state.assets[index].files.splice(fileIndex, 1)
	}
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations
};
